import { delay } from 'redux-saga'
import normalize from 'jsonapi-normalizer'
import T from 'i18n-react'
import {
  takeLatest,
  take,
  fork,
  all,
  call,
  select,
  put,
} from 'redux-saga/effects'

import {
  getRequest,
  post,
} from 'helpers/RequestUtil'

import {
  countSelector,
  pageSizeSelector,
  currentPageSelector,
  searchValueSelector,
} from './selectors'

import {
  CHANGE_CURRENT_PAGE,
  CHANGE_PAGE_SIZE,
  CHANGE_SEARCH_VALUE,
  CHANGE_SELECT_USER,
  FETCH_USERS,
  POST_USER
} from './constants'

import {
  setActiveDialog,
} from 'containers/Admin/User/User/actions'

import {
  success,
  error,
} from 'containers/App/Notification/actions'

import {
  updatePageSize,
  updateCountSize,
  updateCurrentPage,
  updateSearchValue,
  clearUserList,
  updateUserList,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersError,
  startFetchUsersAsync,
  postUserStart,
  postUserSuccess,
  postUserError,
  startPostUserAsync,
} from './actions'

import {
  updateTablesAction,
} from 'containers/Entity/actions'

function* postUser({id, userId, username, ...rest}, role) {
  try {
    yield put(startPostUserAsync())
    yield put(postUserStart())
    const data = yield call(
      post,
      'roles',
      { role, username },
      `/api/fulcrum/users/${userId}/roles`
    )
    const normalized = {
      result: { users: [id] },
      entities: { users: { [id]: { ...rest, id, userId, username, role, } } }
    }
    yield put(updateTablesAction(normalized))
    yield put(postUserSuccess())
    yield put(success(T.translate(
      'pages.admin.users.notification.post_success',
      {
        name: rest.name,
        role
      }
    )))
    return data
  } catch(err) {
    yield put(postUserError(err))
    yield put(error(T.translate('pages.admin.users.notification.post_error', {name: rest.name})))
  }
}

function* fetchUsers(page, size, search) {
  try {
    yield put(startFetchUsersAsync())
    yield put(fetchUsersStart())
    const data = yield call(
      getRequest,
      `/api/fulcrum/users?page[number]=${page}&page[size]=${size}&filter[search]=${search}`
    )
    const normalized = normalize(data)
    yield put(updateTablesAction(normalized))
    yield put(fetchUsersSuccess())
    return data
  } catch(error) {
    yield put(fetchUsersError(error))
  }
}

function* handlePostUser(action) {
  const { user, role } = action
  yield call(postUser, user, role)
}

function* handleFetchUsers() {
  yield call(delay, 500)
  const page = yield select(currentPageSelector)
  const size = yield select(pageSizeSelector)
  const search = yield select(searchValueSelector)
  const data = yield call(fetchUsers, page, size, search)
  const normalized = normalize(data)
  const { result: { users } } = normalized
  yield put(updateCountSize(data.meta.totalCount))
  yield put(updateUserList(users))
}

function* handleChangePageSize(action) {
  const { size } = action
  const page = yield select(currentPageSelector)
  const prevSize = yield select(pageSizeSelector)
  let newPage = Math.floor((page - 1) * prevSize / size)
  newPage = (newPage > 0)
    ? newPage
    : newPage + 1

  yield put(updatePageSize(size))
  yield put(updateCurrentPage(newPage))
  yield fork(handleFetchUsers)
}

function* handleChangeCurrentPage(action) {
  const { value } = action
  const page = yield select(currentPageSelector)
  yield put(updateCurrentPage(page + value))
  yield fork(handleFetchUsers)
}

function* handleSearchChange(action) {
  const { value } = action
  yield put(updateCurrentPage(1))
  yield put(clearUserList())
  yield put(updateSearchValue(value))
  yield fork(handleFetchUsers)
}

function* watchPostUser() {
  yield takeLatest(POST_USER, handlePostUser)
}

function* watchFetchUsers() {
  yield takeLatest(FETCH_USERS, handleFetchUsers)
}

function* watchChangePageSize() {
  yield takeLatest(CHANGE_PAGE_SIZE, handleChangePageSize)
}

function* watchChangeCurrentPage() {
  yield takeLatest(CHANGE_CURRENT_PAGE, handleChangeCurrentPage)
}

function* watchSearchChange() {
  yield takeLatest(CHANGE_SEARCH_VALUE, handleSearchChange)
}

function* loadSagas() {
  yield all([
    fork(watchFetchUsers),
    fork(watchChangePageSize),
    fork(watchChangeCurrentPage),
    fork(watchSearchChange),
    fork(watchPostUser),
  ])
}

export default loadSagas
